import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useMenu, useQueue, useReadyLines, useRsMutation, type QueueLine, type Station } from '../api/restaurant';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

type Tab = Station | 'READY';

const ageMinutes = (iso: string | null, now: number): number => (iso ? Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000)) : 0);

/** A short two-note chime. Browsers only allow sound after a tap, so failures are ignored. */
function chime(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    [660, 880].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.value = 0.15;
      o.connect(g);
      g.connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.18);
      o.stop(ctx.currentTime + i * 0.18 + 0.15);
    });
    setTimeout(() => void ctx.close(), 800);
  } catch {
    /* no sound available */
  }
}

export function Kitchen() {
  const { me, can } = useAuth();
  const [tab, setTab] = useState<Tab>(me?.role === 'KITCHEN' ? 'KITCHEN' : can('kitchen.update') ? 'KITCHEN' : 'READY');
  const [now, setNow] = useState(() => Date.now());
  const [soldOut, setSoldOut] = useState(false);
  const [refusing, setRefusing] = useState<QueueLine | null>(null);
  const kitchen = useQueue('KITCHEN');
  const bar = useQueue('BAR');
  const ready = useReadyLines();
  const toast = useToast();
  const report = useReport();

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // chime when a new ticket arrives at the station being watched
  const q = tab === 'KITCHEN' ? kitchen : tab === 'BAR' ? bar : ready;
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = new Set((q.data ?? []).map((l) => l.id));
    if (seen.current && [...ids].some((i) => !seen.current!.has(i)) && tab !== 'READY') chime();
    if (q.data) seen.current = ids;
  }, [q.data, tab]);
  useEffect(() => { seen.current = null; }, [tab]);

  const act = useRsMutation((v: { id: string; path: 'accept' | 'ready' | 'serve' | 'reject'; reason?: string }) => api(`/order-lines/${v.id}/${v.path}`, { method: 'POST', body: v.reason ? { reason: v.reason } : {} }));
  const go = (l: QueueLine, path: 'accept' | 'ready' | 'serve', done?: string) => act.mutate({ id: l.id, path }, { onError: report, onSuccess: () => done && toast.ok(done) });

  // A finished item moves to "Ready to serve"; the stations only show what still has to be made.
  const todo = (d?: QueueLine[]) => (d ?? []).filter((l) => l.status !== 'READY');
  const lines = tab === 'READY' ? (q.data ?? []) : todo(q.data);
  const count = (d?: QueueLine[]) => d?.length ?? 0;

  return (
    <div className="page">
      <PageHead eyebrow="Restaurant and bar" title="Kitchen">
        {can('kitchen.update') ? <Button onClick={() => setSoldOut(true)}>Sold-out list</Button> : null}
      </PageHead>

      <div className="seg" role="group" aria-label="Station">
        <button type="button" aria-pressed={tab === 'KITCHEN'} onClick={() => setTab('KITCHEN')}>Kitchen <span className="num muted">{todo(kitchen.data).length}</span></button>
        <button type="button" aria-pressed={tab === 'BAR'} onClick={() => setTab('BAR')}>Bar <span className="num muted">{todo(bar.data).length}</span></button>
        <button type="button" aria-pressed={tab === 'READY'} onClick={() => setTab('READY')}>Ready to serve <span className="num muted">{count(ready.data)}</span></button>
      </div>

      {q.isLoading ? (
        <Loading rows={3} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : lines.length === 0 ? (
        <div className="card flat">
          <Empty icon="check" title={tab === 'READY' ? 'Nothing waiting to be served' : 'No orders waiting'}>This updates by itself every few seconds.</Empty>
        </div>
      ) : (
        <div className="tickets">
          {lines.map((l) => {
            const mins = ageMinutes(tab === 'READY' ? l.readyAt : l.sentAt, now);
            const late = tab !== 'READY' && mins >= 15;
            return (
              <article key={l.id} className={`ticket-card${late ? ' late' : ''}`}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>{l.where}</strong>
                  <span className={`num small ${late ? 'late-text' : 'muted'}`}>{mins} min</span>
                </div>
                <div style={{ fontSize: '1.15rem' }}>
                  <strong className="num">{l.qty}×</strong> {l.itemName}
                  {l.variantName && l.variantName !== 'Regular' ? <span className="muted"> · {l.variantName}</span> : null}
                </div>
                {l.modifiers.length > 0 ? <div className="note">{l.modifiers.map((m) => m.name).join(' · ')}</div> : null}
                {l.comboName ? <div className="small muted">{l.comboName}</div> : null}
                {l.note ? <div className="note">“{l.note}”</div> : null}
                <div className="row small muted">
                  <span className="num">{l.orderRef}</span>
                  <Badge tone={l.status === 'READY' ? 'good' : l.status === 'ACCEPTED' ? 'warn' : 'info'}>{l.status === 'READY' ? 'Ready' : l.status === 'ACCEPTED' ? 'Cooking' : 'New'}</Badge>
                  {tab === 'READY' ? <span>{l.station === 'BAR' ? 'Bar' : 'Kitchen'}</span> : null}
                </div>
                <div className="row">
                  {tab === 'READY' ? (
                    can('order.modify') ? <Button variant="primary" loading={act.isPending} onClick={() => go(l, 'serve', 'Marked as served')}>Served</Button> : null
                  ) : can('kitchen.update') ? (
                    <>
                      {l.status === 'QUEUED' ? <Button loading={act.isPending} onClick={() => go(l, 'accept')}>Start</Button> : null}
                      <Button variant="primary" loading={act.isPending} onClick={() => go(l, 'ready', 'Ready for the waiter')}>Ready</Button>
                      <Button variant="ghost" onClick={() => setRefusing(l)}>Can’t make</Button>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <RefuseDialog line={refusing} onClose={() => setRefusing(null)} />
      <SoldOutDialog open={soldOut} onClose={() => setSoldOut(false)} />
    </div>
  );
}

function RefuseDialog({ line, onClose }: { line: QueueLine | null; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const toast = useToast();
  const report = useReport();
  const reject = useRsMutation((v: { id: string; reason: string }) => api(`/order-lines/${v.id}/reject`, { method: 'POST', body: { reason: v.reason } }));
  useEffect(() => { if (!line) setReason(''); }, [line]);
  return (
    <Modal open={!!line} onClose={onClose} title={line ? `Can’t make ${line.itemName}?` : 'Refuse'}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (line && reason.trim().length >= 3) reject.mutate({ id: line.id, reason: reason.trim() }, { onError: report, onSuccess: () => { toast.ok('The waiter will see why'); onClose(); } });
        }}
      >
        <Field id="rf" label="Tell the waiter why" hint="e.g. Finished, out of chicken">
          <input id="rf" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Back</Button>
          <Button variant="danger" type="submit" disabled={reason.trim().length < 3} loading={reject.isPending}>Refuse item</Button>
        </div>
      </form>
    </Modal>
  );
}

function SoldOutDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const menu = useMenu(false);
  const report = useReport();
  const toggle = useRsMutation((v: { id: string; soldOut: boolean }) => api(`/menu/items/${v.id}/sold-out`, { method: 'PATCH', body: { soldOut: v.soldOut } }));
  return (
    <Modal open={open} onClose={onClose} title="Sold out today">
      <div className="stack">
        <p className="small muted">Sold-out items cannot be ordered until you switch them back on.</p>
        {(menu.data ?? []).map((c) => (
          <div key={c.id} className="stack">
            <div className="eyebrow">{c.name}</div>
            <ul className="checklist">
              {c.items.map((i) => (
                <li key={i.id}>
                  <label>
                    <input type="checkbox" checked={i.soldOut} disabled={toggle.isPending} onChange={(e) => toggle.mutate({ id: i.id, soldOut: e.target.checked }, { onError: report })} />
                    <span>{i.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <Button block onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}
